import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ticketService } from '../services/api';
import { Plus, Search, Filter, ChevronRight, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import './Tickets.css';

const STATUSES = ['', 'Open', 'In Progress', 'Awaiting Client', 'Awaiting Internal', 'Resolved', 'Closed'];
const PRIORITIES = ['', 'Critical', 'High', 'Medium', 'Low'];

const priorityBadge = (p) => {
  const cls = { Critical: 'badge-critical', High: 'badge-high', Medium: 'badge-medium', Low: 'badge-low' };
  return <span className={`badge ${cls[p]}`}>{p}</span>;
};

const statusBadge = (s) => {
  const cls = {
    Open: 'badge-open', 'In Progress': 'badge-in-progress', Resolved: 'badge-resolved',
    Closed: 'badge-closed', 'Awaiting Client': 'badge-awaiting-client'
  };
  return <span className={`badge ${cls[s] || ''}`}>{s}</span>;
};

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: '', priority: '' });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const params = { page, limit: 20, ...filters };
    if (search) params.search = search;
    Object.keys(params).forEach(k => !params[k] && delete params[k]);
    const res = await ticketService.getAll(params);
    setTickets(res.data.tickets);
    setTotal(res.data.total);
  }, [page, filters, search]);

  useEffect(() => { load(); }, [load]);

  const isSlaWarning = (ticket) => {
    if (!ticket.sla?.resolutionDueAt) return false;
    return new Date(ticket.sla.resolutionDueAt) <= new Date(Date.now() + 24 * 60 * 60 * 1000)
      && !['Resolved', 'Closed'].includes(ticket.status);
  };

  return (
    <div className="tickets-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Tickets</h1>
          <p className="page-subtitle">{total} total tickets</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/tickets/new')}>
          <Plus size={14} /> New Ticket
        </button>
      </div>

      <div className="tickets-toolbar">
        <div className="search-box">
          <Search size={14} />
          <input placeholder="Search tickets..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="filters">
          <Filter size={14} color="var(--text-muted)" />
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            {STATUSES.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
          </select>
          <select value={filters.priority} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))}>
            {PRIORITIES.map(p => <option key={p} value={p}>{p || 'All Priorities'}</option>)}
          </select>
        </div>
      </div>

      <div className="ticket-table card">
        <table>
          <thead>
            <tr>
              <th>Ticket #</th>
              <th>Title</th>
              <th>Client</th>
              <th>App</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Assigned</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tickets.map(t => (
              <tr key={t._id} className={`ticket-row ${isSlaWarning(t) ? 'ticket-row--sla-warn' : ''}`}
                onClick={() => navigate(`/tickets/${t._id}`)}>
                <td><span className="ticket-num">{t.ticketNumber}</span></td>
                <td>
                  <div className="ticket-title">{t.title}</div>
                  <div className="ticket-category">{t.category}</div>
                </td>
                <td>{t.client?.name}</td>
                <td>{t.app?.name}</td>
                <td>{priorityBadge(t.priority)}</td>
                <td>{statusBadge(t.status)}</td>
                <td>{t.assignedTo?.name || <span className="text-muted">Unassigned</span>}</td>
                <td>
                  <div className="ticket-time">
                    {isSlaWarning(t) && <Clock size={12} color="var(--warning)" />}
                    {formatDistanceToNow(new Date(t.createdAt), { addSuffix: true })}
                  </div>
                </td>
                <td><ChevronRight size={16} color="var(--text-muted)" /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {tickets.length === 0 && <div className="empty-state">No tickets found</div>}
      </div>
    </div>
  );
}
